import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/lib/apiClient'

export type Availability = 'available' | 'low' | 'sold_out'

export type PricingStrategy =
  | 'base_price'
  | 'included_items'
  | 'package_price'
  | 'custom_combination'

export interface PublicDrinkCategory {
  id: string
  name: string
  description: string
  position: number
}

export interface PublicDrinkItem {
  id: string
  category_id: string | null
  name: string
  description: string
  brand: string | null
  size_label: string | null
  image_url: string | null
  price_minor: number
  currency: string
  /** Softs, eau, mixers : ils ne consomment jamais la franchise bouteilles. */
  is_extra: boolean
  /**
   * Ne se vend qu'a la commande, a l'unite, en dehors des tables.
   *
   * On ne garde pas ces bouteilles en salle « au cas ou » : il faut les
   * avoir reservees. A ne pas confondre avec `is_extra`.
   */
  order_only: boolean
  availability: Availability
}

/** Une bouteille de la table affichée : la boisson elle-même, et combien. */
export interface PublicCompositionItem {
  drink_item_id: string
  label: string
  quantity: number
}

export interface PublicTablePackage {
  id: string
  name: string
  description: string
  package_price_minor: number
  currency: string
  contents: string[]
  items: PublicCompositionItem[]
}

export interface PublicTableType {
  id: string
  name: string
  description: string
  image_url: string | null
  capacity_min: number
  capacity_max: number
  pricing_strategy: PricingStrategy
  base_price_minor: number
  currency: string
  included_bottles_allowance: number
  min_spend_minor: number | null
  /** RULE 5 : faux sauf décision explicite de l'équipe. */
  includes_admission: boolean
  availability: Availability
  remaining: number | null
  included: string[]
  /** Les mêmes bouteilles que `included`, lisibles par le personnalisateur. */
  included_items: PublicCompositionItem[]
  packages: PublicTablePackage[]
}

export interface PublicDrinkCatalog {
  categories: PublicDrinkCategory[]
  drinks: PublicDrinkItem[]
  table_types: PublicTableType[]
  admission_notice: string | null
}

export function usePublicDrinks(slug: string | undefined) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: ['drinks', slug, i18n.language],
    queryFn: () =>
      apiClient.get<PublicDrinkCatalog>(`/public/events/${slug}/drinks`, {
        locale: i18n.language,
      }),
    enabled: Boolean(slug),
  })
}
